import { motion } from "framer-motion";
import { Button } from "./ui/button";

const ZODIAC_GLYPHS = [
  "\u2648\uFE0E", "\u2649\uFE0E", "\u264A\uFE0E", "\u264B\uFE0E", "\u264C\uFE0E", "\u264D\uFE0E",
  "\u264E\uFE0E", "\u264F\uFE0E", "\u2650\uFE0E", "\u2651\uFE0E", "\u2652\uFE0E", "\u2653\uFE0E",
];

const ZodiacWheel = () => {
  return (
    <div
      aria-hidden
      className="absolute inset-0 flex items-center justify-center pointer-events-none"
    >
      <div className="relative w-[min(96vw,860px)] aspect-square">
        {/* Two thin concentric rings */}
        <div className="absolute inset-0 rounded-full border border-stardust/[0.10]" />
        <div className="absolute inset-[8%] rounded-full border border-stardust/[0.06]" />
        <div className="absolute inset-[24%] rounded-full border border-white/[0.025]" />
        <div className="absolute inset-[40%] rounded-full border border-white/[0.02]" />

        {/* Center subtle glow + dot */}
        <div className="absolute inset-[42%] rounded-full bg-stardust/[0.05] blur-2xl" />
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-1.5 w-1.5 rounded-full bg-stardust/80 shadow-[0_0_14px_rgba(226,194,117,0.6)]" />

        {/* 12 thin spoke ticks */}
        {Array.from({ length: 12 }).map((_, i) => (
          <div
            key={`tick-${i}`}
            className="absolute left-1/2 top-1/2 origin-top h-[50%] w-px bg-white/[0.025]"
            style={{ transform: `translate(-50%, 0) rotate(${i * 30}deg)` }}
          />
        ))}

        {/* Uniform purple zodiac chips on outer ring */}
        {ZODIAC_GLYPHS.map((g, i) => {
          const angle = (i / 12) * Math.PI * 2 - Math.PI / 2;
          const r = 47;
          const x = 50 + r * Math.cos(angle);
          const y = 50 + r * Math.sin(angle);
          return (
            <div
              key={g}
              className="absolute -translate-x-1/2 -translate-y-1/2"
              style={{ left: `${x}%`, top: `${y}%` }}
            >
              <div
                className="flex items-center justify-center h-7 w-7 sm:h-9 sm:w-9 rounded-[6px] text-[#B89BE6] text-xs sm:text-base"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(91, 70, 145, 0.22) 0%, rgba(58, 42, 102, 0.18) 100%)",
                  border: "1px solid rgba(150, 120, 220, 0.18)",
                  boxShadow:
                    "inset 0 0 8px rgba(120, 90, 200, 0.08), 0 0 14px rgba(91, 70, 145, 0.12)",
                  fontFamily: "'DM Serif Display', 'Cormorant Garamond', serif",
                  fontVariantEmoji: "text",
                }}
              >
                <span className="block leading-none">{g}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const Hero = ({ onBegin, onBeginPair }) => {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col items-center justify-center text-center px-6 py-20 overflow-hidden"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.4, ease: "easeOut" }}
      >
        <ZodiacWheel />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: "easeOut", delay: 0.2 }}
        className="relative z-10 flex flex-col items-center max-w-3xl px-4"
      >
        <p
          data-testid="hero-overline"
          className="text-stardust/60 uppercase tracking-[0.5em] sm:tracking-[0.65em] text-[9px] sm:text-[11px] mb-8 sm:mb-12 pl-[0.5em]"
        >
          An astral almanac
        </p>

        <h1
          data-testid="hero-title"
          className="font-display text-[3.25rem] sm:text-7xl md:text-8xl leading-[0.95] text-[#F5EBD8] mb-8 sm:mb-12"
        >
          Astrarium<span className="text-stardust">.</span>
        </h1>

        <h2 className="heading-serif-italic text-slate-300/90 text-lg sm:text-2xl md:text-3xl leading-[1.4] max-w-2xl mb-10 sm:mb-14 px-2 font-normal">
          A free, personal astrology reading by date of birth. Love, career, family, finances, and the year ahead. All in one almanac.
        </h2>

        <div className="flex flex-col sm:flex-row items-center gap-4">
          <Button
            data-testid="hero-begin-button"
            onClick={onBegin}
            className="bg-stardust hover:bg-stardust-light text-cosmic-900 font-medium uppercase tracking-[0.3em] sm:tracking-[0.4em] text-[10px] sm:text-xs px-10 sm:px-16 py-6 sm:py-8 rounded-md shadow-[0_0_50px_rgba(226,194,117,0.28)] hover:shadow-[0_0_60px_rgba(226,194,117,0.45)] transition-all duration-300"
          >
            Reading for me
            <span className="ml-3 sm:ml-4 text-cosmic-900">✦</span>
          </Button>
          <Button
            data-testid="hero-begin-pair-button"
            onClick={onBeginPair}
            variant="outline"
            className="border-stardust/40 text-stardust hover:bg-stardust hover:text-cosmic-900 hover:border-stardust font-medium uppercase tracking-[0.3em] sm:tracking-[0.4em] text-[10px] sm:text-xs px-10 sm:px-16 py-6 sm:py-8 rounded-md transition-all duration-300"
          >
            Reading for us
            <span className="ml-3 sm:ml-4">✦</span>
          </Button>
        </div>

        <div className="mt-12 sm:mt-16 flex items-center gap-4 sm:gap-10 text-slate-500 text-[9px] sm:text-xs uppercase tracking-[0.25em] sm:tracking-[0.3em] flex-wrap justify-center">
          <span>No sign-up</span>
          <span className="text-stardust/40">·</span>
          <span>Solo or couples</span>
          <span className="text-stardust/40">·</span>
          <span>AI-divined</span>
        </div>
      </motion.div>
    </section>
  );
};
